import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, FileJson, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const field = "w-full rounded-md border border-white/10 bg-[#0E121A] px-3 py-2 text-sm text-slate-200 focus:border-sky-500/60 focus:outline-none";

export default function Trace() {
  const [phone, setPhone] = useState("+918238364783");
  const [start, setStart] = useState("2026-10-14T13:59:00");
  const [end, setEnd] = useState("2026-10-14T15:35:59");
  const [busy, setBusy] = useState("");
  const [envInfo, setEnvInfo] = useState(null);

  useEffect(() => {
    api.get("/admin/env").then(({ data }) => setEnvInfo(data)).catch(() => setEnvInfo(null));
  }, []);

  const download = async (format) => {
    setBusy(format);
    try {
      const { data, headers } = await api.get("/admin/trace", {
        params: { phone: phone.replace(/[\s-]/g, ""), start: `${start}+00:00`, end: `${end}+00:00`, format },
        responseType: "blob",
      });
      const name = (headers["content-disposition"] || "").split("filename=")[1] || `dicta_trace.${format}`;
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
      toast.success("Trace downloaded");
    } catch (e) {
      let msg = "Could not build the trace.";
      try { msg = formatApiErrorDetail(JSON.parse(await e.response.data.text()).detail) || msg; } catch (_) { /* keep default */ }
      toast.error(msg);
    } finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen bg-[#05070C] text-slate-200" data-testid="trace-page">
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <Link to="/" data-testid="trace-back-link" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-400 transition-colors duration-200 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </Link>
        <h1 className="font-display text-3xl font-bold text-slate-100 sm:text-4xl">Conversation trace</h1>
        <p className="mt-2 text-sm text-slate-400">One number, one time window: messages, records and backend log lines. Admins only; every read is audited.</p>
        {envInfo && (
          <div data-testid="trace-env-header" className="mt-4 rounded-md border border-white/10 bg-[#0E121A] px-3 py-2 text-xs text-slate-400">
            <span data-testid="trace-env">env <b className="text-slate-200">{envInfo.env}</b></span>{" · "}
            <span data-testid="trace-deploy">deploy <b className="text-slate-200">{envInfo.deploy}</b></span>{" · "}
            <span data-testid="trace-db">db <b className="text-slate-200">{envInfo.db}</b></span>{" · "}
            <span data-testid="trace-host">host <b className="text-slate-200">{envInfo.host}</b></span>{" · "}
            <span data-testid="trace-window">window <b className="text-slate-200">{start}Z to {end}Z</b></span>
            <p data-testid="trace-since-note" className="mt-1 text-amber-300/80">Turn trace records exist only from this deploy onwards (process started {envInfo.traces_since}).</p>
          </div>
        )}
        <div className="mt-8 space-y-4 rounded-lg border border-white/10 bg-[#111723] p-5">
          <label className="block text-xs text-slate-400">WhatsApp number
            <input data-testid="trace-phone-input" className={`${field} mt-1`} value={phone} onChange={(e) => setPhone(e.target.value)} />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-xs text-slate-400">Start (UTC)
              <input data-testid="trace-start-input" type="datetime-local" step="1" className={`${field} mt-1`} value={start} onChange={(e) => setStart(e.target.value)} />
            </label>
            <label className="block text-xs text-slate-400">End (UTC)
              <input data-testid="trace-end-input" type="datetime-local" step="1" className={`${field} mt-1`} value={end} onChange={(e) => setEnd(e.target.value)} />
            </label>
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            <button data-testid="trace-download-xlsx" disabled={!!busy} onClick={() => download("xlsx")}
              className="inline-flex items-center gap-2 rounded-md bg-emerald-600/80 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-600 disabled:opacity-50">
              {busy === "xlsx" ? <Download className="h-4 w-4 animate-bounce" /> : <FileSpreadsheet className="h-4 w-4" />} Excel
            </button>
            <button data-testid="trace-download-json" disabled={!!busy} onClick={() => download("json")}
              className="inline-flex items-center gap-2 rounded-md bg-sky-600/80 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sky-600 disabled:opacity-50">
              {busy === "json" ? <Download className="h-4 w-4 animate-bounce" /> : <FileJson className="h-4 w-4" />} JSON
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
